import React, { useEffect } from "react";
import { StepName, StepProps } from "../../types";
import { useWebTranslation } from "translations";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import LassieLoader from "ui-kit/LassieLoader";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import { getCompetitorName } from "components/Signup/components/utils/insurely/insurelyUtil";
import { updateInsuranceById } from "components/Signup/store/slices/animals";
import { useIsLoading } from "components/Signup/store/selectors/metadata";

const CancelPreviousInsurance = ({ next, transitioning }: StepProps) => {
  const { t } = useWebTranslation();

  const dispatch = useSignupDispatch();
  const animal = useCurrentAnimal();
  const insurance = useCurrentInsuranceByAnimalId(animal.id);
  const isLoading = useIsLoading();

  // always reset cancel competitor when entering this step
  useEffect(() => {
    dispatch(
      updateInsuranceById(
        {
          id: insurance.id,
          update: {
            insurely: {
              ...insurance.insurely,
              cancelCompetitor: false,
            },
          },
        },
        { refetch: false }
      )
    );
  }, []);

  const options: {
    label: string;
    value: boolean;
    id: string;
    loading: boolean;
    onPress?: () => void;
    nextStep: StepName;
  }[] = [
    {
      label: t("global.yes"),
      value: true,
      id: "power-of-attorney",
      loading: false,
      onPress: async () => {
        // quote will be created in next step when user have provided power of attorney
        dispatch(
          updateInsuranceById(
            {
              id: insurance.id,
              update: {
                insurely: {
                  ...insurance.insurely,
                  cancelCompetitor: true,
                },
              },
            },
            { refetch: false }
          )
        );
      },
      nextStep: "power-of-attorney",
    },
    {
      label: t("global.no"),
      value: false,
      id: "no-power-of-attorney",
      loading: isLoading,
      onPress: async () => {
        // next step is payment, but we need to create a quote first
        dispatch(
          updateInsuranceById(
            {
              id: insurance.id,
              update: {
                insurely: {
                  ...insurance.insurely,
                  cancelCompetitor: false,
                  customerSignature: null,
                },
              },
            },
            { refetch: false }
          )
        );
      },
      nextStep: "user-info",
    },
  ];

  const competitorName = getCompetitorName(insurance.insurely);

  return (
    <>
      <ChatContainer leaving={transitioning}>
        <ChatMessage>
          <Typography color="black">
            {t("cancel_competitor.cancel_yes_no.msg_1", {
              competitorName: competitorName ?? "",
            })}
          </Typography>
        </ChatMessage>

        <Gutter size={2} />
        {options.map((item) => (
          <ChatMessage
            fromMe
            key={item.id}
            blink
            onPress={async () => {
              item.onPress && (await item.onPress());
              next(false, item.nextStep);
            }}
          >
            <div data-test-id={item.id} key={item.label}>
              {item.loading && <LassieLoader />}
              {!item.loading && item.label}
            </div>
          </ChatMessage>
        ))}
      </ChatContainer>
    </>
  );
};

export default CancelPreviousInsurance;
